import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router';
import grievanceService from '../../../../services/grievanceService';

import useService from '../../../../shared/hooks/useService';
import { NumberRoute } from '../../../../types';
import Context from './DetailsContext';
import { SpinnerArea } from '../../../../shared/components';

const GrievanceDetailsContextProvider: React.FC<NumberRoute> = ({
  number: grievanceNumber,
  children,
}) => {
  const navigate = useNavigate();
  const [loadingData, setLoadingData] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);

  const loadData = useCallback(() => {
    setLoadingData(true);
    return Promise.all([
      grievanceService.getGrievanceDetailsContext(grievanceNumber),
      grievanceService.getGrievance(grievanceNumber),
    ])
      .then(([detailsData, grievance]) => ({ detailsData, grievance }))
      .finally(() => {
        setLoadingData(false);
        setHasAttempted(true);
      });
  }, [grievanceNumber]);

  const [detailsData, loading, setData] = useService(loadData);

  useEffect(() => {
    if (hasAttempted && !loadingData && !detailsData) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 500);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [hasAttempted, loadingData, detailsData, navigate]);

  const reload = useCallback(() => {
    // eslint-disable-next-line no-console
    loadData().then(setData).catch(console.error);
  }, [loadData, setData]);

  const reloadPhases = useCallback(() => {
    if (!detailsData) {
      return;
    }

    setLoadingData(true);
    grievanceService
      .getGrievancePhases(detailsData.grievance.number)
      .then((phases) => {
        setData({
          ...detailsData,
          detailsData: {
            ...detailsData.detailsData,
            phases,
          },
        });
      })
      // eslint-disable-next-line no-console
      .catch(console.error)
      .finally(() => setLoadingData(false));
  }, [detailsData, grievanceNumber]);

  useEffect(() => {
    if (grievanceNumber && detailsData && detailsData.grievance.number !== grievanceNumber) {
      reload();
    }
  }, [grievanceNumber]);

  const value = useMemo(
    () =>
      detailsData && {
        grievanceNumber,
        grievance: detailsData.grievance,
        grievancePhaseData: detailsData.detailsData.detailData,
        phases: detailsData.detailsData.phases,
        lookupsClosureTypes: detailsData.detailsData.lookupsClosureTypes.filter((x) =>
          detailsData.detailsData.detailData.isDiscontinued ? x.isDiscontinueManagement : true
        ),
        lookupsDocumentTypes: detailsData.detailsData.lookupsDocumentTypes,
        reload,
        reloadPhases,
        loading: loading || loadingData,
      },
    [detailsData, grievanceNumber, reload, reloadPhases, loading, loadingData]
  );

  return (
    <SpinnerArea loading={loading || loadingData}>
      {value && <Context.Provider value={value}>{children}</Context.Provider>}
    </SpinnerArea>
  );
};

export default GrievanceDetailsContextProvider;
